import { Helmet } from 'react-helmet-async';

// Enhanced Knowledge Graph Schema for WordLift compatibility
// This creates interconnected entities that form a semantic knowledge graph

export function EnhancedKnowledgeGraph() {
  const knowledgeGraph = {
    "@context": {
      "@vocab": "https://schema.org/",
      "gtin13": "https://schema.org/gtin13",
      "review": { "@id": "https://schema.org/review", "@type": "@id" },
      "brand": { "@id": "https://schema.org/brand", "@type": "@id" }
    },
    "@graph": [
      // Main Organization Entity
      {
        "@type": ["Organization", "LocalBusiness", "FurnitureStore"],
        "@id": "https://www.modularfurniturestore.com/#organization",
        "name": "Modular Furniture Store",
        "legalName": "Modular Furniture Store",
        "alternateName": ["MFS", "Modular Furniture India", "MFS Mumbai"],
        "description": "India's leading manufacturer of premium modular office furniture including workstations, privacy booths, phone pods, and storage solutions. Made in India with free delivery in Mumbai.",
        "url": "https://www.modularfurniturestore.com",
        "logo": {
          "@type": "ImageObject",
          "@id": "https://www.modularfurniturestore.com/#logo",
          "url": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png",
          "contentUrl": "https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png",
          "width": 512,
          "height": 512,
          "caption": "Modular Furniture Store Logo"
        },
        "image": {
          "@id": "https://www.modularfurniturestore.com/#logo"
        },
        "foundingDate": "2020",
        "foundingLocation": {
          "@id": "https://www.modularfurniturestore.com/#mumbai"
        },
        "slogan": "Kholo, Jodo, Aur Lagalo - Open, Join, Install",
        "telephone": "+91-95946-85818",
        "email": "hello@modularfurniturestore.com",
        "address": {
          "@id": "https://www.modularfurniturestore.com/#address"
        },
        "location": {
          "@id": "https://www.modularfurniturestore.com/#mumbai"
        },
        "areaServed": [
          { "@id": "https://www.modularfurniturestore.com/#mumbai" },
          { "@id": "https://www.modularfurniturestore.com/#india" }
        ],
        "knowsAbout": [
          { "@id": "https://www.modularfurniturestore.com/#concept-modular-furniture" },
          { "@id": "https://www.modularfurniturestore.com/#concept-office-workstation" },
          { "@id": "https://www.modularfurniturestore.com/#concept-privacy-booth" },
          { "@id": "https://www.modularfurniturestore.com/#concept-acoustic-pod" }
        ],
        "makesOffer": [
          { "@id": "https://www.modularfurniturestore.com/#offer-workstations" },
          { "@id": "https://www.modularfurniturestore.com/#offer-privacy-booths" },
          { "@id": "https://www.modularfurniturestore.com/#offer-storage" }
        ],
        "hasOfferCatalog": {
          "@id": "https://www.modularfurniturestore.com/#catalog"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "250",
          "bestRating": "5",
          "worstRating": "1"
        },
        "sameAs": [
          "https://www.modularfurniturestore.com"
        ]
      },

      // Address Entity
      {
        "@type": "PostalAddress",
        "@id": "https://www.modularfurniturestore.com/#address",
        "streetAddress": "Mumbai",
        "addressLocality": "Mumbai",
        "addressRegion": "Maharashtra",
        "postalCode": "400001",
        "addressCountry": {
          "@id": "https://www.modularfurniturestore.com/#india"
        }
      },

      // Mumbai Place Entity
      {
        "@type": "City",
        "@id": "https://www.modularfurniturestore.com/#mumbai",
        "name": "Mumbai",
        "alternateName": ["Bombay", "Mumbai City"],
        "containedInPlace": {
          "@type": "State",
          "name": "Maharashtra",
          "containedInPlace": {
            "@id": "https://www.modularfurniturestore.com/#india"
          }
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 19.0760,
          "longitude": 72.8777
        }
      },

      // India Country Entity
      {
        "@type": "Country",
        "@id": "https://www.modularfurniturestore.com/#india",
        "name": "India",
        "alternateName": "IN"
      },

      // Concept Entities for Knowledge Graph
      {
        "@type": "DefinedTerm",
        "@id": "https://www.modularfurniturestore.com/#concept-modular-furniture",
        "name": "Modular Furniture",
        "description": "Furniture designed with standardized units or sections for easy assembly, reconfiguration, and customization. Popular in modern office spaces for flexibility and space optimization.",
        "inDefinedTermSet": "Office Furniture Terminology"
      },
      {
        "@type": "DefinedTerm",
        "@id": "https://www.modularfurniturestore.com/#concept-office-workstation",
        "name": "Office Workstation",
        "description": "A designated workspace typically including a desk, storage, and partitions for individual employees. Modern workstations are designed for ergonomics and productivity.",
        "inDefinedTermSet": "Office Furniture Terminology"
      },
      {
        "@type": "DefinedTerm",
        "@id": "https://www.modularfurniturestore.com/#concept-privacy-booth",
        "name": "Privacy Booth",
        "description": "An enclosed workspace pod designed for private phone calls, video conferences, or focused work. Features soundproofing, ventilation, and power outlets.",
        "inDefinedTermSet": "Office Furniture Terminology"
      },
      {
        "@type": "DefinedTerm",
        "@id": "https://www.modularfurniturestore.com/#concept-acoustic-pod",
        "name": "Acoustic Pod",
        "description": "A soundproof enclosure for offices providing 30-40 dB noise reduction. Used for private conversations and focused work in open-plan offices.",
        "inDefinedTermSet": "Office Furniture Terminology"
      },

      // Product Catalog
      {
        "@type": "OfferCatalog",
        "@id": "https://www.modularfurniturestore.com/#catalog",
        "name": "Modular Furniture Store Product Catalog",
        "itemListElement": [
          {
            "@type": "OfferCatalog",
            "@id": "https://www.modularfurniturestore.com/#offer-workstations",
            "name": "Modular Office Workstations",
            "description": "Linear, L-shaped, and cluster workstations for modern offices",
            "url": "https://www.modularfurniturestore.com/products?category=Modular%20Office%20Workstation"
          },
          {
            "@type": "OfferCatalog",
            "@id": "https://www.modularfurniturestore.com/#offer-privacy-booths",
            "name": "Privacy Booths & Phone Pods",
            "description": "Soundproof phone pods, meeting booths, and focus pods",
            "url": "https://www.modularfurniturestore.com/products?category=Privacy%20Booths"
          },
          {
            "@type": "OfferCatalog",
            "@id": "https://www.modularfurniturestore.com/#offer-storage",
            "name": "Storage Cabinets",
            "description": "Filing cabinets, credenzas, and under-desk storage solutions",
            "url": "https://www.modularfurniturestore.com/products?category=Storage%20Cabinets"
          }
        ]
      },

      // Service Entities
      {
        "@type": "Service",
        "@id": "https://www.modularfurniturestore.com/#service-delivery",
        "name": "Office Furniture Delivery",
        "description": "Pan India delivery with free shipping in Mumbai and Navi Mumbai",
        "provider": {
          "@id": "https://www.modularfurniturestore.com/#organization"
        },
        "areaServed": {
          "@id": "https://www.modularfurniturestore.com/#india"
        },
        "serviceType": "Furniture Delivery"
      },
      {
        "@type": "Service",
        "@id": "https://www.modularfurniturestore.com/#service-installation",
        "name": "Professional Installation",
        "description": "Free professional installation in Mumbai and Navi Mumbai. Tool-free assembly with Kholo, Jodo, Aur Lagalo method.",
        "provider": {
          "@id": "https://www.modularfurniturestore.com/#organization"
        },
        "areaServed": {
          "@id": "https://www.modularfurniturestore.com/#mumbai"
        },
        "serviceType": "Furniture Installation"
      },

      // WebSite Entity
      {
        "@type": "WebSite",
        "@id": "https://www.modularfurniturestore.com/#website",
        "name": "Modular Furniture Store",
        "url": "https://www.modularfurniturestore.com",
        "publisher": {
          "@id": "https://www.modularfurniturestore.com/#organization"
        },
        "inLanguage": "en-IN",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://www.modularfurniturestore.com/products?search={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(knowledgeGraph)}
      </script>
    </Helmet>
  );
}

// Product Entity with full Knowledge Graph connections
export function ProductKnowledgeGraph({ product }) {
  if (!product) return null;

  // Calculate price valid date (30 days from now)
  const priceValidDate = new Date();
  priceValidDate.setDate(priceValidDate.getDate() + 30);
  const priceValidUntil = priceValidDate.toISOString().split('T')[0];

  const productEntity = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `https://www.modularfurniturestore.com/products/${product.id}#product`,
    "name": product.name,
    "description": product.description,
    "image": product.image_urls || [product.image_url],
    "sku": product.id,
    "mpn": product.id?.substring(0, 12),
    "category": product.category,
    "brand": {
      "@id": "https://www.modularfurniturestore.com/#organization"
    },
    "manufacturer": {
      "@id": "https://www.modularfurniturestore.com/#organization"
    },
    "countryOfOrigin": {
      "@id": "https://www.modularfurniturestore.com/#india"
    },
    "isRelatedTo": [
      { "@id": "https://www.modularfurniturestore.com/#concept-modular-furniture" }
    ],
    "offers": {
      "@type": "Offer",
      "@id": `https://www.modularfurniturestore.com/products/${product.id}#offer`,
      "url": `https://www.modularfurniturestore.com/products/${product.id}`,
      "priceCurrency": "INR",
      "price": product.price,
      "priceValidUntil": priceValidUntil,
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@id": "https://www.modularfurniturestore.com/#organization"
      },
      "deliveryLeadTime": {
        "@type": "QuantitativeValue",
        "minValue": 7,
        "maxValue": 25,
        "unitCode": "d"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "IN"
        },
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": 0,
          "currency": "INR"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 7,
            "unitCode": "d"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 3,
            "maxValue": 18,
            "unitCode": "d"
          }
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Verified Buyer"
        },
        "reviewBody": "Excellent quality and easy assembly. The modular design made installation quick."
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(productEntity)}
      </script>
    </Helmet>
  );
}

export default EnhancedKnowledgeGraph;
